import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  LOCALE_PATHS,
  isLocalizedPath,
  type AppLanguage,
} from '@/shared/i18n/localeRoutes';

/**
 * The one place that decides what changing language means.
 *
 * On a localized route the language IS the URL, so switching navigates and lets
 * the route drive i18n. Everywhere else there is no locale URL to move to, so
 * the language changes in place exactly as it always has.
 *
 * The choice is stored either way, so app routes remember what was picked on
 * the landing page.
 */
export function useLanguageSwitch(): (lang: AppLanguage) => void {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useCallback(
    (lang: AppLanguage) => {
      try {
        localStorage.setItem('language', lang);
      } catch (error) {
        console.error('Error writing language to localStorage:', error);
      }

      if (isLocalizedPath(pathname)) {
        navigate(LOCALE_PATHS[lang]);
        return;
      }

      void i18n.changeLanguage(lang);
    },
    [i18n, navigate, pathname],
  );
}
